import { useState, useEffect, useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { MessageList } from './MessageList';

import { MessageInput } from './MessageInput';

import { fetchMessages, markConversationRead } from '../../../redux/actions/chatRealtime';

import { emitReadReceipt } from '../../../services/socket';

import { useSendMessage } from '../../../hooks/useSendMessage';

import { useSubsessionState } from '../../../hooks/useSubsessions';

import { ChatHeaderTitle } from './ChatHeaderTitle';
import { buildCourseParticipants } from './buildCourseParticipants';

import './chat.css';



export const ChatSidebarPanel = ({ courseId, headerAction = null }) => {

    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);

    const { courseSelected, courseListSelected } = useSelector((state) => state.course);

    const {

        activeConversationId,

        messages,

        participants,

        typingUsers,

        connected,

    } = useSelector((state) => state.chatRealtime);

    /** Clase en curso ahora mismo: el servidor ya la limita al horario de la clase. */
    const { currentLiveSession } = useSelector((state) => state.zoom);

    const classIsLive = Boolean(currentLiveSession);



    const [replyTo, setReplyTo] = useState(null);

    const [nextCursor, setNextCursor] = useState(null);

    const [hasMore, setHasMore] = useState(false);

    const [loadingMore, setLoadingMore] = useState(false);

    const [localComposing, setLocalComposing] = useState(false);

    const { sendMessage, retryMessage } = useSendMessage(courseId);

    const { currentSubsessionName } = useSubsessionState();

    /** Avisar de la caída solo si dura: al cargar el socket tarda un instante en conectar. */
    const [connectionLost, setConnectionLost] = useState(false);

    useEffect(() => {

        if (connected) {

            setConnectionLost(false);

            return undefined;

        }

        const timer = setTimeout(() => setConnectionLost(true), 1500);

        return () => clearTimeout(timer);

    }, [connected]);



    useEffect(() => {

        setNextCursor(null);

        setHasMore(false);



        if (!activeConversationId) return;



        dispatch(fetchMessages(activeConversationId)).then((data) => {

            if (!data) return;

            setNextCursor(data.nextCursor);

            setHasMore(Boolean(data.hasMore ?? data.nextCursor));

        });

    }, [activeConversationId, dispatch]);



    useEffect(() => {

        if (messages.length && activeConversationId && courseId) {

            const lastConfirmed = [...messages]

                .reverse()

                .find((m) => m._id && !String(m._id).startsWith('tmp-') && m.status !== 'sending' && m.status !== 'failed');

            if (lastConfirmed) {

                emitReadReceipt(courseId, activeConversationId, lastConfirmed._id);

                dispatch(markConversationRead(activeConversationId, lastConfirmed._id));

            }

        }

    }, [messages, activeConversationId, courseId, dispatch]);



    const mentionParticipants = useMemo(
        () => buildCourseParticipants({
            chatParticipants: participants,
            course: courseSelected,
            students: courseListSelected,
        }),
        [participants, courseSelected, courseListSelected],
    );



    const handleLoadMore = useCallback(async () => {

        if (!nextCursor || !activeConversationId || loadingMore) return;



        setLoadingMore(true);

        try {

            const data = await dispatch(fetchMessages(activeConversationId, nextCursor));

            if (!data) return;

            setNextCursor(data.nextCursor);

            setHasMore(Boolean(data.hasMore ?? data.nextCursor));

        } finally {

            setLoadingMore(false);

        }

    }, [nextCursor, activeConversationId, loadingMore, dispatch]);



    return (

        <div className="flex flex-col h-full min-h-0 bg-white">

            <header className="chat-header flex-shrink-0">

                <div className="flex items-center gap-2 min-w-0">

                    <ChatHeaderTitle course={courseSelected} />

                    {currentSubsessionName && (

                        <span className="chat-badge" title={`Estás en ${currentSubsessionName}`}>

                            {currentSubsessionName}

                        </span>

                    )}

                </div>

                <div className="chat-header__actions">

                    <span className="chat-header__status" aria-live="polite">

                        {connectionLost ? (

                            <span className="chat-status chat-status--offline">

                                <span className="chat-status__dot" />

                                Sin conexión

                            </span>

                        ) : classIsLive ? (

                            <span className="chat-status chat-status--live">

                                <span className="chat-status__dot" />

                                En vivo

                            </span>

                        ) : null}

                    </span>

                    {headerAction}

                </div>

            </header>



            <MessageList

                conversationId={activeConversationId}

                messages={messages}

                currentUserId={user?._id}

                onReply={setReplyTo}

                onRetry={retryMessage}

                onLoadMore={handleLoadMore}

                hasMore={hasMore}

                loadingMore={loadingMore}

                participants={mentionParticipants}

                typingUsers={typingUsers}

                localComposing={localComposing}

            />



            <MessageInput

                courseId={courseId}

                conversationId={activeConversationId}

                replyTo={replyTo}

                onCancelReply={() => setReplyTo(null)}

                participants={mentionParticipants}

                currentUserId={user?._id}

                onSend={sendMessage}

                onComposingChange={setLocalComposing}

            />

        </div>

    );

};


